import { BillingProduct, BillingSubscription } from '@pollination-solutions/pollination-sdk'

// Currency formatting helpers
export const getCurrencySymbol = (currencyCode: string): string => {
  const symbols: { [key: string]: string } = {
    'USD': '$',
    'EUR': '€',
    'GBP': '£',
    'JPY': '¥',
    'CAD': 'C$',
    'AUD': 'A$',
    'CHF': 'CHF',
    'CNY': '¥',
    'SEK': 'kr',
    'NOK': 'kr',
    'DKK': 'kr',
    'PLN': 'zł',
    'CZK': 'Kč',
    'HUF': 'Ft',
    'RUB': '₽',
    'INR': '₹',
    'BRL': 'R$',
    'ZAR': 'R',
    'KRW': '₩',
    'SGD': 'S$',
    'HKD': 'HK$',
    'NZD': 'NZ$',
    'MXN': '$',
    'THB': '฿',
    'TRY': '₺'
  }
  return symbols[currencyCode] || currencyCode
}

export const formatCurrency = (amount: string | number, currencyCode: string): string => {
  let numAmount = typeof amount === 'string' ? parseFloat(amount) : amount
  numAmount = numAmount / 100 // Convert cents to dollars
  const symbol = getCurrencySymbol(currencyCode)

  // Format with accounting style (comma separators, 2 decimal places)
  const formatted = numAmount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })

  return `${symbol}${formatted}`
}

// Extract plan_multiplier from price data
export const getPlanMultiplier = (price: any): number => {
  return price.custom_data?.['plan_multiplier'] || 1
}

// Helper to determine if a price is monthly or yearly based on billing cycle
export const getPricePeriod = (price: any): 'monthly' | 'yearly' => {
  const interval = price.billing_cycle?.interval?.toLowerCase() || 'month'
  return interval.includes('year') ? 'yearly' : 'monthly'
}

// Get the billing frequency of a subscription based on existing items
export const getSubscriptionBillingFrequency = (subscription: any): 'monthly' | 'yearly' => {
  if (!subscription.items || subscription.items.length === 0) {
    return 'monthly' // Default to monthly for empty subscriptions
  }

  const frequencies = subscription.items.map((item: any) => getPricePeriod(item.price))
  const uniqueFrequencies = Array.from(new Set(frequencies))

  if (uniqueFrequencies.length === 1) {
    return uniqueFrequencies[0] as 'monthly' | 'yearly'
  }

  throw new Error('Mixed billing frequencies detected in subscription items')
}

// Determine resource type based on product name
export const getResourceType = (product: BillingProduct | undefined): { type: string; singular: string; plural: string } => {
  const productName = product?.name.toLowerCase() || ''

  if (productName.includes('seat')) {
    return { type: 'seat', singular: 'organization seat', plural: 'organization seats' }
  } else if (productName.includes('rhino') && !productName.includes('bundled')) {
    return { type: 'rhino_license', singular: 'Rhino plugin license', plural: 'Rhino plugin licenses' }
  } else if (productName.includes('revit') && !productName.includes('bundled')) {
    return { type: 'revit_license', singular: 'Revit plugin license', plural: 'Revit plugin licenses' }
  } else if (productName.includes('bundled')) {
    return { type: 'bundled_license', singular: 'bundled plugin license', plural: 'bundled plugin licenses' }
  } else {
    return { type: 'resource', singular: 'resource', plural: 'resources' }
  }
}

// Calculate total resources with proper resource type aggregation
export const calculateResourcesByType = (
  products: BillingProduct[], 
  items: any[], 
  editedItems: { [itemId: string]: { quantity: number; priceId?: string } }, 
  newItems: { productId?: string; priceId?: string; quantity: number; isEditing: boolean }[], 
  removedItems: Set<number>
): { [key: string]: { count: number; type: ReturnType<typeof getResourceType> } } => {
  const resourcesByType: { [key: string]: { count: number; type: ReturnType<typeof getResourceType> } } = {}

  // Count existing items with edits, excluding removed items
  items?.forEach((item: any, index: number) => {
    // Skip removed items
    if (removedItems.has(index)) return

    const editedItem = editedItems[index]
    const quantity = editedItem?.quantity || item.quantity
    if (quantity > 0) { // Skip items with zero quantity
      const multiplier = getPlanMultiplier(item.price)
      const resourceType = getResourceType(item.product)
      const totalForThisItem = multiplier * quantity

      // For bundled licenses, count both Rhino and Revit licenses
      if (resourceType.type === 'bundled_license') {
        // Add Rhino licenses
        if (!resourcesByType['rhino_license']) {
          resourcesByType['rhino_license'] = {
            count: 0,
            type: { type: 'rhino_license', singular: 'Rhino plugin license', plural: 'Rhino plugin licenses' }
          }
        }
        resourcesByType['rhino_license'].count += totalForThisItem

        // Add Revit licenses
        if (!resourcesByType['revit_license']) {
          resourcesByType['revit_license'] = {
            count: 0,
            type: { type: 'revit_license', singular: 'Revit plugin license', plural: 'Revit plugin licenses' }
          }
        }
        resourcesByType['revit_license'].count += totalForThisItem
      } else {
        if (!resourcesByType[resourceType.type]) {
          resourcesByType[resourceType.type] = { count: 0, type: resourceType }
        }
        resourcesByType[resourceType.type].count += totalForThisItem
      }
    }
  })

  // Count new items
  newItems.forEach(newItem => {
    if (newItem.productId && newItem.priceId) {
      const product = products.find(p => p.id === newItem.productId)
      const price = product?.prices?.find(p => p.id === newItem.priceId)
      if (price && product) {
        const multiplier = getPlanMultiplier(price)
        const resourceType = getResourceType(product)
        const totalForThisItem = multiplier * newItem.quantity

        // For bundled licenses, count both Rhino and Revit licenses
        if (resourceType.type === 'bundled_license') {
          // Add Rhino licenses
          if (!resourcesByType['rhino_license']) {
            resourcesByType['rhino_license'] = {
              count: 0,
              type: { type: 'rhino_license', singular: 'Rhino plugin license', plural: 'Rhino plugin licenses' }
            }
          }
          resourcesByType['rhino_license'].count += totalForThisItem

          // Add Revit licenses
          if (!resourcesByType['revit_license']) {
            resourcesByType['revit_license'] = {
              count: 0,
              type: { type: 'revit_license', singular: 'Revit plugin license', plural: 'Revit plugin licenses' }
            }
          }
          resourcesByType['revit_license'].count += totalForThisItem
        } else {
          if (!resourcesByType[resourceType.type]) {
            resourcesByType[resourceType.type] = { count: 0, type: resourceType }
          }
          resourcesByType[resourceType.type].count += totalForThisItem
        }
      }
    }
  })

  return resourcesByType
}

// Check if a product is already used in other subscriptions (excluding current subscription being edited)
export const hasConflictingProduct = (
  productId: string,
  currentSubscriptionId: string,
  existingSubscriptions: BillingSubscription[]
): boolean => {
  return existingSubscriptions.some(sub =>
    sub.id !== currentSubscriptionId &&
    sub.status === 'active' &&
    sub.items?.some(item => item.product?.id === productId)
  )
}

// Check if product has already used all prices in existing subscription
export const hasUnusedPrices = (
  product: BillingProduct,
  selectedPrices: Set<string>,
  billingFrequency: 'monthly' | 'yearly',
): boolean => {
  const availablePrices = product.prices?.filter(p =>
    !selectedPrices.has(p.id) && p.billing_cycle?.interval + 'ly' === billingFrequency
  )
  return availablePrices ? availablePrices.length > 0 : false
}